/**
 * Service for managing file uploads and downloads via the ColdFusion REST API
 * Base URL: https://apicabaza.creago.be/rest/cabaza/files
 */

import { getAuthHeaders } from './authService';

const API_BASE_URL = `${import.meta.env.VITE_API_BASE_URL}/files`;
const FILES_BASE_URL = import.meta.env.VITE_FILES_BASE_URL;

// Max file size for images (50 KB)
const MAX_IMAGE_SIZE = 50 * 1024;
// Max dimension for images
const MAX_IMAGE_DIMENSION = 800;

/**
 * Compress an image file to fit within size and dimension limits
 * @param {File} file - Original image file
 * @param {number} maxSize - Maximum file size in bytes (default 50KB)
 * @param {number} maxDimension - Maximum width/height in pixels (default 800)
 * @returns {Promise<File>} Compressed image file
 */
export const compressImage = async (file, maxSize = MAX_IMAGE_SIZE, maxDimension = MAX_IMAGE_DIMENSION) => {
    return new Promise((resolve, reject) => {
        // If not an image, return as-is
        if (!file.type.startsWith('image/')) {
            resolve(file);
            return;
        }

        const img = new Image();
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        img.onload = () => {
            // Calculate new dimensions while maintaining aspect ratio
            let { width, height } = img;

            if (width > maxDimension || height > maxDimension) {
                if (width > height) {
                    height = Math.round((height * maxDimension) / width);
                    width = maxDimension;
                } else {
                    width = Math.round((width * maxDimension) / height);
                    height = maxDimension;
                }
            }

            canvas.width = width;
            canvas.height = height;

            // Draw image on canvas
            ctx.drawImage(img, 0, 0, width, height);

            // Try different quality levels to achieve target size
            const tryCompress = (quality) => {
                canvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            reject(new Error('Failed to compress image'));
                            return;
                        }

                        // If size is acceptable or quality is at minimum, return
                        if (blob.size <= maxSize || quality <= 0.1) {
                            const compressedFile = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), {
                                type: 'image/jpeg',
                                lastModified: Date.now()
                            });
                            console.log(`Image compressed: ${file.size} -> ${compressedFile.size} bytes (quality: ${quality})`);
                            resolve(compressedFile);
                        } else {
                            // Try lower quality
                            tryCompress(quality - 0.1);
                        }
                    },
                    'image/jpeg',
                    quality
                );
            };

            // Start with 0.8 quality
            tryCompress(0.8);
        };

        img.onerror = () => {
            reject(new Error('Failed to load image'));
        };

        // Load image from file
        const reader = new FileReader();
        reader.onload = (e) => {
            img.src = e.target.result;
        };
        reader.onerror = () => {
            reject(new Error('Failed to read file'));
        };
        reader.readAsDataURL(file);
    });
};

/**
 * Upload an image with automatic compression
 * @param {File} file - Image file to upload
 * @param {string} path - Storage path (e.g., 'peoples/{uuid}/')
 * @param {Object} options - Additional options
 * @param {string} options.fileType - File type category
 * @param {string} options.entityType - Entity type (peoples, places, etc.)
 * @param {string} options.entityUuid - Entity UUID
 * @param {number} options.entityId - Entity ID
 * @param {number} options.uploadedById - Uploader's user ID
 * @param {string} options.description - File description
 * @param {number} options.maxSize - Max file size (default 50KB)
 * @returns {Promise<Object>} Upload result
 */
export const uploadImage = async (file, path, options = {}) => {
    try {
        const maxSize = options.maxSize || MAX_IMAGE_SIZE;

        // Compress image if needed
        let fileToUpload = file;
        if (file.type.startsWith('image/') && file.size > maxSize) {
            console.log(`Compressing image from ${file.size} bytes...`);
            fileToUpload = await compressImage(file, maxSize);
        }

        // Prepare form data
        const formData = new FormData();
        formData.append('file', fileToUpload);
        formData.append('path', path);

        if (options.fileType) formData.append('fileType', options.fileType);
        if (options.entityType) formData.append('entityType', options.entityType);
        if (options.entityUuid) formData.append('entityUuid', options.entityUuid);
        if (options.entityId) formData.append('entityId', options.entityId.toString());
        if (options.uploadedById) formData.append('uploadedById', options.uploadedById.toString());
        if (options.description) formData.append('description', options.description);

        const response = await fetch(`${API_BASE_URL}/upload`, {
            method: 'POST',
            body: formData
        });

        const result = await handleResponse(response);
        return result;

    } catch (error) {
        console.error('Error uploading image:', error);
        throw error;
    }
};

/**
 * Helper function to handle API responses
 */
const handleResponse = async (response) => {
    const responseText = await response.text();

    // Handle empty body (HTTP 204 No Content or empty responses)
    if (!responseText || responseText.trim() === '') {
        if (response.ok || response.status === 204) {
            return { success: true };
        }
        throw new Error(`HTTP error with empty body! status: ${response.status}`);
    }

    let result;
    try {
        result = JSON.parse(responseText);
    } catch (parseError) {
        console.error('Failed to parse JSON:', parseError);
        throw new Error(`Invalid JSON response: ${responseText}`);
    }

    if (!response.ok) {
        const error = new Error(result.message || `HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.data = result;
        throw error;
    }

    return result;
};

/**
 * Get all files with optional filters
 * @param {Object} options - Filter options
 * @param {string} options.entityType - Entity type (peoples, places, etc.)
 * @param {number} options.entityId - Entity ID
 * @param {string} options.entityUuid - Entity UUID
 * @param {string} options.fileType - File type (profile_photo, document, etc.)
 * @param {number} options.uploadedById - Uploader's ID
 * @param {string} options.status - Status filter (1 for active)
 * @returns {Promise<Object>} Response with files data
 */
export const getFiles = async (options = {}) => {
    try {
        const params = new URLSearchParams();

        if (options.entityType) params.append('entityType', options.entityType);
        if (options.entityId) params.append('entityId', options.entityId.toString());
        if (options.entityUuid) params.append('entityUuid', options.entityUuid);
        if (options.fileType) params.append('fileType', options.fileType);
        if (options.uploadedById) params.append('uploadedById', options.uploadedById.toString());
        if (options.status !== undefined) params.append('status', options.status);

        const url = params.toString() ? `${API_BASE_URL}?${params}` : API_BASE_URL;
        console.log('Fetching files from:', url);

        const response = await fetch(url, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await handleResponse(response);
        return result;

    } catch (error) {
        console.error('Error fetching files:', error);
        throw error;
    }
};

/**
 * Get a single file by ID
 * @param {number} fileId - File ID
 * @returns {Promise<Object>} File data
 */
export const getFileById = async (fileId) => {
    try {
        console.log('Fetching file by ID:', fileId);

        const response = await fetch(`${API_BASE_URL}/${fileId}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await handleResponse(response);
        return result;

    } catch (error) {
        console.error('Error fetching file:', error);
        throw error;
    }
};

/**
 * Get profile photo for a user
 * @param {string} peopleUuid - User's UUID
 * @returns {Promise<Object>} Profile photo data or null
 */
export const getProfilePhoto = async (peopleUuid) => {
    try {
        console.log('Fetching profile photo for:', peopleUuid);

        const response = await fetch(`${API_BASE_URL}/profile-photo/${peopleUuid}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await handleResponse(response);
        return result;

    } catch (error) {
        console.error('Error fetching profile photo:', error);
        throw error;
    }
};

/**
 * Upload a file
 * @param {File} file - File to upload
 * @param {Object} options - Upload options
 * @param {string} options.path - Relative path for storage
 * @param {string} options.fileType - File type category
 * @param {string} options.entityType - Entity type (peoples, places, etc.)
 * @param {number} options.entityId - Entity ID
 * @param {string} options.entityUuid - Entity UUID
 * @param {number} options.uploadedById - Uploader's user ID
 * @param {string} options.description - File description
 * @returns {Promise<Object>} Upload result with file info
 */
export const uploadFile = async (file, options = {}) => {
    try {
        console.log('Uploading file:', file.name, options);

        const formData = new FormData();
        formData.append('file', file);

        if (options.path) formData.append('path', options.path);
        if (options.fileType) formData.append('fileType', options.fileType);
        if (options.entityType) formData.append('entityType', options.entityType);
        if (options.entityId) formData.append('entityId', options.entityId.toString());
        if (options.entityUuid) formData.append('entityUuid', options.entityUuid);
        if (options.uploadedById) formData.append('uploadedById', options.uploadedById.toString());
        if (options.description) formData.append('description', options.description);

        const response = await fetch(`${API_BASE_URL}/upload`, {
            method: 'POST',
            body: formData
        });

        const result = await handleResponse(response);
        return result;

    } catch (error) {
        console.error('Error uploading file:', error);
        throw error;
    }
};

/**
 * Upload a profile photo (with automatic compression to max 50KB)
 * @param {File} file - Image file to upload
 * @param {string} peopleUuid - User's UUID
 * @param {number} peopleId - User's ID
 * @param {number} uploadedById - Uploader's user ID
 * @returns {Promise<Object>} Upload result with photo info
 */
export const uploadProfilePhoto = async (file, peopleUuid, peopleId, uploadedById) => {
    try {
        console.log('Uploading profile photo for:', peopleUuid);

        // Validate file type
        const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            throw new Error('Type de fichier non supporté. Utilisez JPG, PNG, GIF ou WEBP.');
        }

        // Compress image if needed
        let fileToUpload = file;
        if (file.size > MAX_IMAGE_SIZE) {
            console.log(`Compressing image from ${file.size} bytes...`);
            fileToUpload = await compressImage(file, MAX_IMAGE_SIZE);
        }

        // Prepare form data for profile-photo endpoint
        const formData = new FormData();
        formData.append('file', fileToUpload);
        formData.append('peopleUuid', peopleUuid);
        if (peopleId) formData.append('peopleId', peopleId.toString());
        if (uploadedById) formData.append('uploadedById', uploadedById.toString());

        // Use /profile-photo endpoint which handles deletion of old photos
        const response = await fetch(`${API_BASE_URL}/profile-photo`, {
            method: 'POST',
            body: formData
        });

        const result = await handleResponse(response);
        return result;

    } catch (error) {
        console.error('Error uploading profile photo:', error);
        throw error;
    }
};

/**
 * Update file metadata
 * @param {number} fileId - File ID
 * @param {Object} data - Updated data
 * @param {string} data.originalName - New file name
 * @param {string} data.description - New description
 * @param {boolean} data.status - Active status
 * @returns {Promise<Object>} Update result
 */
export const updateFile = async (fileId, data) => {
    try {
        console.log('Updating file:', fileId, data);

        const response = await fetch(`${API_BASE_URL}/${fileId}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            credentials: 'include',
            body: JSON.stringify(data)
        });

        const result = await handleResponse(response);
        console.log('File updated:', result);
        return result;

    } catch (error) {
        console.error('Error updating file:', error);
        throw error;
    }
};

/**
 * Delete a file
 * @param {number} fileId - File ID
 * @returns {Promise<Object>} Deletion result
 */
export const deleteFile = async (fileId) => {
    try {
        console.log('Deleting file:', fileId);

        const response = await fetch(`${API_BASE_URL}/${fileId}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const result = await handleResponse(response);
        console.log('File deleted:', result);
        return result;

    } catch (error) {
        console.error('Error deleting file:', error);
        throw error;
    }
};

/**
 * Get the full URL for a file
 * @param {string} filePath - Relative file path from API
 * @returns {string} Full URL to the file
 */
export const getFileUrl = (filePath) => {
    if (!filePath) return null;
    // If already a full URL, return as-is
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
        return filePath;
    }
    // Build full URL
    return `${FILES_BASE_URL}/${filePath.replace(/^\//, '')}`;
};

/**
 * Get profile photo URL for a user
 * @param {string} peopleUuid - User's UUID
 * @param {string} filePath - File path from API (optional)
 * @returns {string|null} Full URL to profile photo or null
 */
export const getProfilePhotoUrl = (peopleUuid, filePath) => {
    if (filePath) {
        return getFileUrl(filePath);
    }
    return null;
};

// Document types constants
export const DOCUMENT_TYPES = {
    CV: 'cv',
    BUSINESS_CARD: 'business_card',
    OTHER: 'other'
};

export const DOCUMENT_TYPE_LABELS = {
    [DOCUMENT_TYPES.CV]: 'CV',
    [DOCUMENT_TYPES.BUSINESS_CARD]: 'Carte de visite',
    [DOCUMENT_TYPES.OTHER]: 'Autre document'
};

/**
 * Upload a document (CV, business card, etc.)
 * @param {File} file - File to upload
 * @param {string} peopleUuid - User's UUID
 * @param {number} peopleId - User's ID
 * @param {string} documentType - Type of document (cv, business_card, other)
 * @param {number} uploadedById - Uploader's user ID
 * @param {string} description - Optional description
 * @returns {Promise<Object>} Upload result with file info
 */
export const uploadDocument = async (file, peopleUuid, peopleId, documentType, uploadedById, description = '') => {
    try {
        console.log('Uploading document:', file.name, documentType, 'for:', peopleUuid);

        // Validate file type based on document type
        const imageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        const documentTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

        let validTypes;
        let maxSize;

        if (documentType === DOCUMENT_TYPES.BUSINESS_CARD) {
            validTypes = imageTypes;
            maxSize = 5 * 1024 * 1024; // 5MB for images
        } else if (documentType === DOCUMENT_TYPES.CV) {
            validTypes = [...documentTypes, ...imageTypes];
            maxSize = 10 * 1024 * 1024; // 10MB for CV
        } else {
            validTypes = [...documentTypes, ...imageTypes];
            maxSize = 10 * 1024 * 1024; // 10MB for other
        }

        if (!validTypes.includes(file.type)) {
            if (documentType === DOCUMENT_TYPES.BUSINESS_CARD) {
                throw new Error('Type de fichier non supporté. Utilisez JPG, PNG, GIF ou WEBP.');
            } else {
                throw new Error('Type de fichier non supporté. Utilisez PDF, DOC, DOCX, JPG, PNG, GIF ou WEBP.');
            }
        }

        if (file.size > maxSize) {
            throw new Error(`Le fichier est trop volumineux. Maximum ${maxSize / (1024 * 1024)} MB.`);
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('path', `peoples/${peopleUuid}/documents/`);
        formData.append('fileType', documentType);
        formData.append('entityType', 'peoples');
        formData.append('entityUuid', peopleUuid);
        if (peopleId) formData.append('entityId', peopleId.toString());
        if (uploadedById) formData.append('uploadedById', uploadedById.toString());
        if (description) formData.append('description', description);

        const response = await fetch(`${API_BASE_URL}/upload`, {
            method: 'POST',
            body: formData
        });

        const result = await handleResponse(response);
        return result;

    } catch (error) {
        console.error('Error uploading document:', error);
        throw error;
    }
};

/**
 * Get documents for a user
 * @param {string} peopleUuid - User's UUID
 * @param {string} documentType - Optional filter by document type
 * @returns {Promise<Object>} Documents list
 */
export const getDocuments = async (peopleUuid, documentType = null) => {
    try {
        console.log('Fetching documents for:', peopleUuid);

        const options = {
            entityType: 'peoples',
            entityUuid: peopleUuid,
            status: '1'
        };

        if (documentType) {
            options.fileType = documentType;
        }

        const result = await getFiles(options);

        // Handle ColdFusion uppercase keys
        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA ?? [];

        // Filter out profile photos
        const documents = (Array.isArray(data) ? data : []).filter(file => {
            const fileType = (file.fileType ?? file.FILETYPE ?? '').toLowerCase();
            return fileType !== 'profile_photo';
        });

        return { success, data: documents };

    } catch (error) {
        console.error('Error fetching documents:', error);
        throw error;
    }
};

// ============================================
// User Preferences JSON File Management
// ============================================

/**
 * Get user preferences from server file via API
 * Endpoint: GET /rest/cabaza/files/preferences/{peopleUuid}
 * @param {string} peopleUuid - User's UUID
 * @returns {Promise<Object|null>} Preferences object or null if not found
 */
export const getUserPreferencesFile = async (peopleUuid) => {
    if (!peopleUuid) {
        console.warn('[filesService] getUserPreferencesFile: No peopleUuid provided');
        return null;
    }

    try {
        console.log('[filesService] Fetching preferences from API for:', peopleUuid);

        const response = await fetch(`${API_BASE_URL}/preferences/${peopleUuid}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        if (!response.ok) {
            if (response.status === 404) {
                console.log('[filesService] No preferences file found for user, will use defaults');
                return null;
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        const success = result.success ?? result.SUCCESS;
        const data = result.data ?? result.DATA;

        if (success && data) {
            console.log('[filesService] Loaded preferences from file');
            return data;
        }

        return null;

    } catch (error) {
        console.error('[filesService] Error fetching user preferences file:', error);
        return null;
    }
};

/**
 * Save user preferences to server file via API
 * Endpoint: PUT /rest/cabaza/files/preferences/{peopleUuid}
 * @param {string} peopleUuid - User's UUID
 * @param {Object} preferences - Preferences object to save
 * @returns {Promise<boolean>} true if saved successfully
 */
export const saveUserPreferencesFile = async (peopleUuid, preferences) => {
    if (!peopleUuid) {
        console.warn('[filesService] saveUserPreferencesFile: No peopleUuid provided');
        return false;
    }

    try {
        console.log('[filesService] Saving preferences to file for:', peopleUuid);

        const response = await fetch(`${API_BASE_URL}/preferences/${peopleUuid}`, {
            method: 'PUT',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(preferences)
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        const success = result.success ?? result.SUCCESS;

        if (success) {
            console.log('[filesService] Preferences saved to file');
            return true;
        }

        console.error('[filesService] Failed to save preferences:', result.message || result.MESSAGE);
        return false;

    } catch (error) {
        console.error('[filesService] Error saving user preferences file:', error);
        return false;
    }
};

/**
 * Get default preferences structure
 * @returns {Object} Default preferences
 */
export const getDefaultPreferences = () => ({
    pinnedItems: [],
    dashboardDesktops: [],
    collapsedSidebarGroups: {},
    notificationPrefs: {
        system: { app: true, email: true },
        contacts: { app: true, email: true },
        messages: { app: true, email: true },
        books: { app: true, email: true },
        meetings: { app: true, email: true },
        agenda: { app: true, email: true },
        map: { app: true, email: true },
        places: { app: true, email: true },
        offers: { app: true, email: true },
        products: { app: true, email: true },
        knowledge: { app: true, email: true },
        moments: { app: true, email: true },
        carpooling: { app: true, email: true },
        work: { app: true, email: true }
    },
    selectedThemeId: 0,
    language: '',
    visibleLanguageCodes: ['fr', 'en', 'nl', 'de', 'es', 'it', 'pt', 'el'],
    showLanguageSwitcher: false,
    moduleGroups: [],
    dockVisible: true,
    dockSize: 'large',
    dockPosition: 'bottom'
});

/**
 * Clean up old localStorage keys after migration
 * Call this after successfully loading preferences from file
 */
export const cleanupOldLocalStorageKeys = () => {
    const keysToRemove = [
        'cabazaGroupsData',
        'cabazaModules',
        'cabazaPinnedItems',
        'cabazaDashboardDesktops',
        'cabazaCollapsedSidebarGroups',
        'cabazaNotificationPrefs',
        'language',
        'cabazaVisibleLanguages',
        'cabazaShowLanguageSwitcher',
        'cabaza_user_contacts',
        'cabaza_user_places',
        'cabaza_notifications'
    ];

    // Also remove cabaza_user_${userId} keys
    const userId = localStorage.getItem('userId');
    if (userId) {
        keysToRemove.push(`cabaza_user_${userId}`);
    }

    keysToRemove.forEach(key => {
        if (localStorage.getItem(key) !== null) {
            console.log('[filesService] Removing old localStorage key:', key);
            localStorage.removeItem(key);
        }
    });

    console.log('[filesService] Old localStorage keys cleaned up');
};
